import { Badge, Button, Card, cn, Input } from "@artisan/ui";
import { Check, ChevronRight, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";

import { PlanView } from "../components/plan-view";
import { Screen } from "../components/screen";
import { addRoom, projectTitle } from "../project/actions";
import { SHAPES } from "../project/model";
import { statusBadge, type StepKey, steps } from "../project/progress";
import { deleteProject, useProject } from "../project/store";
import { errorsOf, reviewRoom } from "../survey/checks";
import { areaM2 } from "../survey/geometry";
import { solveRoom } from "../survey/solve";

const LINKS: Record<StepKey, string> = {
  site: "site",
  rooms: "",
  design: "design",
  render: "render",
  sign: "sign",
  send: "send",
};

function ShapePreview({ sketch }: { sketch: [number, number][] }) {
  const xs = sketch.map((p) => p[0]);
  const ys = sketch.map((p) => p[1]);
  const w = Math.max(...xs);
  const h = Math.max(...ys);
  return (
    <svg viewBox={`-0.3 ${-h - 0.3} ${w + 0.6} ${h + 0.6}`} className="h-14 w-20" aria-hidden>
      <polygon points={sketch.map(([x, y]) => `${x},${-y}`).join(" ")} fill="var(--card)" stroke="currentColor" strokeWidth={0.14} strokeLinejoin="round" />
    </svg>
  );
}

export function ProjectScreen() {
  const { id } = useParams();
  const project = useProject(id);
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [shape, setShape] = useState("rect");
  const [name, setName] = useState("Гал тогоо");
  if (!project) return <Navigate to="/" replace />;
  const current = project;
  const badge = statusBadge(project);
  const list = steps(project);

  async function add() {
    const room = await addRoom(current.id, shape, name.trim() || `Өрөө ${current.rooms.length + 1}`);
    navigate(`/p/${current.id}/rooms/${room.id}`);
  }

  return (
    <Screen
      back="/"
      title={projectTitle(project)}
      subtitle={[project.site.customer_name, project.site.address].filter(Boolean).join(" · ") || "Захиалагчийн мэдээлэл оруулаагүй"}
      actions={
        <>
          <Badge variant={badge.variant}>{badge.label}</Badge>
          <Button size="lg" asChild>
            <Link to={`/p/${project.id}/send`}>Үйлдвэрт илгээх</Link>
          </Button>
        </>
      }
    >
      <ol className="grid grid-cols-6 gap-3" aria-label="Алхмууд">
        {list.map((s, i) => {
          const to = LINKS[s.key] ? `/p/${project.id}/${LINKS[s.key]}` : null;
          const body = (
            <Card className={cn("h-full p-4 transition-shadow", to && "hover:shadow-md", s.done && "border-primary/30")}>
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "grid size-7 place-items-center rounded-full text-xs font-semibold tabular",
                    s.done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                  )}
                >
                  {s.done ? <Check className="size-4" aria-label="Хийгдсэн" /> : i + 1}
                </span>
                {to ? <ChevronRight className="size-4 text-muted-foreground" aria-hidden /> : null}
              </div>
              <p className="mt-3 font-semibold">{s.title}</p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.detail}</p>
            </Card>
          );
          return <li key={s.key}>{to ? <Link to={to} className="block h-full rounded-xl outline-none focus-visible:shadow-ring">{body}</Link> : body}</li>;
        })}
      </ol>

      <section className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-[20px] font-semibold tracking-tight">Өрөөнүүд</h2>
          {!adding ? (
            <Button variant="outline" icon={Plus} onClick={() => setAdding(true)}>
              Өрөө нэмэх
            </Button>
          ) : null}
        </div>
        {adding ? (
          <Card className="mb-5 p-5">
            <p className="mb-3 font-semibold">Өрөөний хэлбэр</p>
            <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Өрөөний хэлбэр">
              {SHAPES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="radio"
                  aria-checked={shape === s.key}
                  onClick={() => setShape(s.key)}
                  className={cn(
                    "flex w-32 flex-col items-center gap-1 rounded-xl border p-3 text-sm",
                    shape === s.key ? "border-primary bg-primary-soft text-primary" : "bg-card text-muted-foreground hover:bg-accent",
                  )}
                >
                  <ShapePreview sketch={s.sketch} />
                  {s.name}
                  <span className="text-xs opacity-70">{s.sketch.length} булан</span>
                </button>
              ))}
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Хэлбэр нь зөвхөн тойм: хэмжээ, өнцгийг таны хэмжилтээс тооцно. Булангуудыг цагийн зүүний дагуу 1-ээс дугаарлана.
            </p>
            <div className="mt-4 flex items-end gap-3">
              <label className="grid flex-1 gap-1 text-xs font-medium text-muted-foreground">
                Өрөөний нэр
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 text-[15px]" />
              </label>
              <Button variant="ghost" size="lg" onClick={() => setAdding(false)}>
                Болих
              </Button>
              <Button size="lg" onClick={() => void add()}>
                Хэмжиж эхлэх
              </Button>
            </div>
          </Card>
        ) : null}
        {project.rooms.length === 0 && !adding ? (
          <Card className="grid place-items-center gap-2 border-dashed py-12 text-center">
            <p className="font-medium">Өрөө нэмээгүй байна</p>
            <p className="text-sm text-muted-foreground">Хэмжих өрөөгөө нэмж, хэлбэрээ сонгоно уу.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-3 gap-5">
            {project.rooms.map((room) => {
              const errors = errorsOf(reviewRoom(room));
              const solved = solveRoom({
                sketch: room.sketch,
                walls: room.walls.map((w) => w?.um ?? null),
                diagonals: room.diagonals.map((d) => ({ a: d.a, b: d.b, um: d.length?.um ?? null })),
              });
              return (
                <Card key={room.id} className="overflow-hidden p-0">
                  <Link to={`/p/${project.id}/rooms/${room.id}`} className="block outline-none focus-visible:shadow-ring">
                    <div className="grid h-44 place-items-center bg-drafting-grid p-3">
                      <PlanView room={room} className="h-full w-full" />
                    </div>
                    <div className="flex items-center justify-between gap-2 p-4">
                      <div className="min-w-0">
                        <p className="truncate font-semibold">{room.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {room.sketch.length} хана
                          {solved.ok ? ` · ${areaM2(solved.at).toFixed(1)} м²` : ""} · {room.zones.length} бүс
                        </p>
                      </div>
                      {errors.length ? <Badge variant="warning">{errors.length} дутуу</Badge> : <Badge variant="success">Бэлэн</Badge>}
                    </div>
                  </Link>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <div className="mt-10 flex justify-end">
        <Button
          variant="ghost"
          icon={Trash2}
          className="text-destructive"
          onClick={async () => {
            if (!window.confirm("Энэ төслийг төхөөрөмжөөс устгах уу? Илгээсэн хувилбарууд сервер дээр үлдэнэ.")) return;
            await deleteProject(project.id);
            navigate("/");
          }}
        >
          Төслийг устгах
        </Button>
      </div>
    </Screen>
  );
}
